import axios from "axios";
import * as _ from "lodash";
import React, { useEffect } from "react";
import { ConversationStep } from "../../interfaces";
import { trackEvent } from "../../store/analytics/action";
import { useAppDispatch } from "../../store/hooks";
import {
    getReadableDate,
    isDaylightSavingTimeUK,
    trackEvent as trackGoogleEvent,
} from "../../util";
import TypingAnimation from "../TypingAnimation";

import ReactPixel from "react-facebook-pixel";

const { REACT_APP_MORTGAGE_URL } = process.env;

interface IProps {
    step?: ConversationStep;
    values?: any;
    triggerNextStep?: (data?: {
        currentStep: ConversationStep;
        value?: string | number;
        trigger: string;
    }) => void;
}

const AppointmentAPICall: React.FC<IProps> = ({
    values,
    step,
    triggerNextStep,
}) => {
    const dispatch = useAppDispatch();

    //get email from keys
    const getEmailValue = (values: any): string => {
        const arrayOfValues = Object.keys(values);
        const emailKey = arrayOfValues.find(
            (data: any) => data.toLowerCase().indexOf("email") > -1
        );
        return emailKey ? values[emailKey] : null;
    };

    const getNames = (values: any): string => {
        const arrayOfValues = Object.keys(values);
        const _firstName: string | any = arrayOfValues.find(
            (data: any) => data.toLowerCase().indexOf("first_name") > -1
        );
        const _lastName: string | any = arrayOfValues.find(
            (data: any) => data.toLowerCase().indexOf("last_name") > -1
        );
        return _firstName || _lastName
            ? `${values[_firstName]} ${values[_lastName]}`
            : "";
    };

    const convertToTimezone = (date: any, targetTimezone: any) => {
        const pad = (number: any) => String(number).padStart(2, "0");

        // Parse the target timezone string
        const timezoneSign = targetTimezone[0];
        const timezoneHours = parseInt(targetTimezone.slice(1, 3), 10);
        const timezoneMinutes = parseInt(targetTimezone.slice(3, 5), 10);

        // Calculate the target timezone offset in minutes
        const targetTimezoneOffset =
            (timezoneHours * 60 + timezoneMinutes) *
            (timezoneSign === "+" ? 1 : -1);

        // Get the local timezone offset in minutes
        const localTimezoneOffset = date.getTimezoneOffset();

        // Calculate the total offset difference
        const totalOffset = targetTimezoneOffset + localTimezoneOffset;

        // Adjust the date by the total offset difference
        const adjustedDate = new Date(date.getTime() + totalOffset * 60000);

        // Extract components of the adjusted date
        const year = adjustedDate.getFullYear();
        const month = pad(adjustedDate.getMonth() + 1);
        const day = pad(adjustedDate.getDate());
        const hours = pad(adjustedDate.getHours());
        const minutes = pad(adjustedDate.getMinutes());
        const seconds = pad(adjustedDate.getSeconds());

        // Combine all parts into the desired format
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${targetTimezone}`;
    };

    const sendAppointmentEmail = async () => {
        const theme = step?.theme!;
        const name = values?.applicant1_firstName
            ? `${values?.applicant1_firstName}`
            : getNames(values);
        const email = values?.applicant1_email
            ? values?.applicant1_email
            : getEmailValue(values);
        const companyLogo = theme?.logo;
        const date = values?.appointment_date?.split("T")[0];
        const time = values?.appointment_date?.split("T")[1]?.split(":");
        const startTime = `${date}T${time[0]}:${time[1]}:00+0${
            isDaylightSavingTimeUK() ? 1 : 0
        }00`;
        let localEndTime = new Date(startTime);
        localEndTime.setMinutes(
            localEndTime.getMinutes() +
                60 * (theme?.workHours?.timeInterval || 0.5)
        );
        //convert to u timezone
        // SWITCH TO NEGATIVE -60 WHEN BST
        // const endTime = `${new Date(localEndTime.getTime() - 0 * 60000)
        //     .toISOString()
        //     .slice(0, -5)}+0${isDaylightSavingTimeUK() ? 1 : 0}00`;

        const endTime = convertToTimezone(
            localEndTime,
            `+0${isDaylightSavingTimeUK() ? 1 : 0}00`
        );
        const _portalName = theme?.emailCustomization?.portalName || "";

        const formData = Object.keys(values)
            .map((key) => {
                if (
                    key === "theme" ||
                    key === "simplified" ||
                    key === "templates" ||
                    key === "property_filtered"
                ) {
                    return null;
                }

                if (
                    key === "mortgage_type" &&
                    (theme.version === "customer-service-questionnaire" ||
                        theme.version === "rightmove")
                ) {
                    return null;
                }

                if (
                    _portalName === "Fair Investment App" &&
                    (key === "payment_method" ||
                        key === "reasonForRemortgage" ||
                        key === "product_type" ||
                        key === "product_term" ||
                        key === "mortgage_class" ||
                        key === "monthly_rent")
                ) {
                    return null;
                }
                if (
                    key === "mortgage_type" &&
                    (theme.version === "customer-service-questionnaire" ||
                        theme.version === "rightmove")
                ) {
                    return null;
                }

                let refinedKey = key;
                if (key.indexOf("]_") > -1) {
                    refinedKey = key.substring(key.indexOf("]_") + 2);
                }
                return {
                    label: _.startCase(refinedKey),
                    value: JSON.stringify(values[key]),
                };
            })
            .filter(Boolean);
        if (
            values?.contact_requested === "Video Call" ||
            theme.version === "rightmove"
        ) {
            //trigger video call appointment
            await axios.post(
                `${REACT_APP_MORTGAGE_URL}/api/teams/scheduleAppointment`,
                {
                    apiKey: theme?.apiKey,
                    name,
                    email,
                    startTime,
                    endTime,
                    props: {
                        startTime: getReadableDate(startTime),
                        endTime: getReadableDate(endTime),
                        email,
                        name,
                        formData,
                        companyLogo,
                        companyName:
                            theme?.customization?.companyName || "Meet Parker",
                        theme: theme?.customization?.primaryColor,
                        botName: theme?.customization?.botName,
                        leadSourceType: values?.contact_requested,
                    },
                    templateName:
                        theme.version === "rightmove"
                            ? "call-appointment"
                            : theme?.appointmentCustomization
                                  ?.appointmentTemplate
                            ? theme?.appointmentCustomization
                                  ?.appointmentTemplate
                            : "video-appointment",
                    emailCustomization: {
                        ...theme?.emailCustomization,
                        subject:
                            theme?.emailCustomization?.subject ||
                            "Mortgage Appointment",
                    },
                    appointmentCustomization: theme?.appointmentCustomization,
                    admin: theme?.email,
                    links: theme?.links,
                }
            );
        } else {
            const defaultTemplate =
                values?.contact_requested === "Telephone Call"
                    ? "call-appointment"
                    : "face-to-face-appointment";
            await axios.post(
                `${REACT_APP_MORTGAGE_URL}/api/teams/requestCallOrFaceToFaceAppointment`,
                {
                    apiKey: theme?.apiKey,
                    name,
                    email,
                    props: {
                        startTime: getReadableDate(startTime),
                        endTime: getReadableDate(endTime),
                        email,
                        name,
                        formData,
                        companyLogo,
                        companyName:
                            theme?.customization?.companyName || "Meet Parker",
                        theme: theme?.customization?.primaryColor,
                        botName: theme?.customization?.botName,
                        leadSourceType: values?.contact_requested,
                    },
                    templateName: theme?.appointmentCustomization
                        ?.appointmentTemplate
                        ? theme?.appointmentCustomization?.appointmentTemplate
                        : defaultTemplate,
                    emailCustomization: {
                        ...theme?.emailCustomization,
                        subject:
                            theme?.emailCustomization?.subject ||
                            "Mortgage Appointment",
                    },
                    appointmentCustomization: theme?.appointmentCustomization,
                    admin: theme?.email,
                    links: theme?.links,
                }
            );
        }

        if (theme?.googleAnalyticsTrackingId) {
            trackGoogleEvent(theme?.googleAnalyticsTrackingId, {
                category: "MeetParkerChat",
                action: "Book Appointment",
            });
        }

        if (theme?.facebookPixelId) {
            ReactPixel.trackCustom("Book Appointment", {
                name: `${values?.applicant1_firstName} ${values?.applicant1_lastName}`,
            });
        }

        dispatch(
            trackEvent({
                appointment: {
                    type: values?.contact_requested,
                    date: startTime,
                },
                lead: values,
            })
        );
    };

    const sendNoAppointmentEmail = async () => {
        const theme = step?.theme!;
        await axios.post(`${REACT_APP_MORTGAGE_URL}/api/teams/noAppointment`, {
            apiKey: theme?.apiKey,
            appointmentCustomization: theme?.appointmentCustomization,
            emailCustomization: theme?.emailCustomization,
            email: values?.applicant1_email
                ? values?.applicant1_email
                : getEmailValue(values),
            props: {
                theme: theme?.customization?.primaryColor,
                name: values?.applicant1_firstName
                    ? `${values?.applicant1_firstName}`
                    : getNames(values),
            },
            links: theme?.links,
        });
    };

    const objectHasThisValue = (obj: any, targetValue: string) => {
        for (let key in obj) {
            if (obj[key] === targetValue) {
                return true; // Value found in one of the properties
            }
        }
        return false; // Value not found in any property
    };

    useEffect(() => {
        const initialize = async () => {
            if (
                (values?.theme?.apiKey ===
                    "b8310d47-ad2d-4365-9d7f-215167f735f7" ||
                    values?.theme?.apiKey ===
                        "b8310d47-ad2d-4365-9d7f-215167f735f8") &&
                objectHasThisValue(values, "Protection Review")
            ) {
                await sendAppointmentEmail();
            } else if (
                values?.theme?.apiKey ===
                    "abde4f0e-b4de-4166-8c87-a0bdf1500bb6" &&
                objectHasThisValue(values, "Book an Appointment")
            ) {
                await sendAppointmentEmail();
            } else if (
                (values?.theme?.apiKey ===
                    "6fda33d3-466e-4f97-b80b-9e55f7a7d1f2" ||
                    values?.theme?.apiKey ===
                        "24397e75-2096-4572-8e54-0cd37856e5b6") &&
                (values?.mortgage_type === "Equity Release Mortgages" ||
                    values?.mortgage_type === "Bridging Finance" ||
                    values["[wdbf]_Type of loan"] === "Talk to an Advisor" ||
                    values["[wdbf]_Type of loan"] === "New or Self Build" ||
                    values["[wdbf]_Type of loan"] === "Refurbishments")
            ) {
                await sendAppointmentEmail();
            } else if (
                values?.talk_with_advisor === "Yes" ||
                values?.how_can_we_help === "Book an appointment" ||
                values?.how_can_i_help_you_next === "book_appointment" ||
                (values?.theme?.apiKey ===
                    "6fda33d3-466e-4f97-b80b-9e55f7a7d1f2" &&
                    (values["How can I help?"] === "Surveys" ||
                        values["How can I help?"] === "Building & Contents")) ||
                values["greeting_1"] === "Book an appointment" ||
                values?.theme?.apiKey ===
                    "cf9a5636-184e-4a86-a553-11a651a6ff05" ||
                values?.theme?.apiKey === "059fe7e9-3c17-4117-b69a-05394d0fa668"
            ) {
                await sendAppointmentEmail();
            } else {
                await sendNoAppointmentEmail();
            }

            triggerNextStep!({
                currentStep: step!,
                trigger: step!.trigger,
            });
        };

        initialize();

        /*eslint-disable react-hooks/exhaustive-deps*/
    }, []);

    if (
        values?.talk_with_advisor === "No" &&
        step?.theme?.apiKey === "d6176c89-bca5-4454-9101-20f15203dc87"
    ) {
        return (
            <div className="conversation-message-bubble">
                <TypingAnimation theme={step?.theme!} type="bot" />
                <div className="conversation-message-bubble-text">
                    We highly recommend seeking good quality advice when it
                    comes to your financial needs. Please feel free to adjust
                    your search parameters above or contact our trusted mortgage
                    advisor partner by clicking the button below and completing
                    their contact us form.
                </div>
            </div>
        );
    }
    if (values?.talk_with_advisor === "No") return null;
    if (values?.contact_requested === "Talk Now") return null;

    return (
        <div className="conversation-message-bubble">
            <TypingAnimation theme={step?.theme!} type="bot" />
            <div className="conversation-message-bubble-text">
                I'm just booking this in for you
            </div>
        </div>
    );
};

export default React.memo(AppointmentAPICall);
